/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { CertificateRecord, CostCenter } from '../types.ts';
import { getCertificateValidityInfo, CertificateValidityInfo } from './certificationsExcelExporter.ts';

export interface EvaluatedCertificateItem {
  certificate: CertificateRecord;
  validity: CertificateValidityInfo;
  costCenterName: string;
}

export interface CertificatesAlertEvaluation {
  totalCertificates: number;
  expiringCount: number; // Documentos que vencen en <= 30 días (y >= 0)
  expiredCount: number;  // Documentos ya vencidos (< 0 días)
  urgentTotalCount: number; // expiringCount + expiredCount
  validCount: number;
  inAuditCount: number;
  expiringCertificates: EvaluatedCertificateItem[];
  expiredCertificates: EvaluatedCertificateItem[];
  urgentCertificates: EvaluatedCertificateItem[]; // Expired first, then expiring soonest
  hasUrgentAlerts: boolean;
  mostUrgentItem: EvaluatedCertificateItem | null;
  evaluationTimestamp: string;
}

/**
 * Loads all certificates across all cost centers and fallback storage slots.
 */
export function loadAllCertificatesFromStorage(costCenters?: CostCenter[]): CertificateRecord[] {
  const allMap = new Map<string, CertificateRecord>();

  try {
    // 1. Scan known cost center prefixes
    if (costCenters && costCenters.length > 0) {
      for (const cc of costCenters) {
        const key = `snf_sec_v3_${cc.id}_certs`;
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              parsed.forEach((cert: CertificateRecord) => {
                if (cert && cert.id) {
                  if (!cert.costCenterName && cc.name) {
                    cert.costCenterName = cc.name;
                  }
                  if (!cert.costCenterId && cc.id) {
                    cert.costCenterId = cc.id;
                  }
                  allMap.set(cert.id, cert);
                }
              });
            }
          } catch (e) {
            console.warn(`Error parsing certificates for cost center ${cc.id}:`, e);
          }
        }
      }
    }

    // 2. Scan all localStorage keys matching the pattern
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('snf_sec_v3_') && key.endsWith('_certs'))) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              parsed.forEach((cert: CertificateRecord) => {
                if (cert && cert.id && !allMap.has(cert.id)) {
                  allMap.set(cert.id, cert);
                }
              });
            }
          } catch (e) {
            // ignore
          }
        }
      }
    }

    // 3. Fallback direct keys
    const fallbackKeys = ['snf_sec_v3_default_certs', 'snf_certificates', 'hseq_certificates_global'];
    for (const key of fallbackKeys) {
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            parsed.forEach((cert: CertificateRecord) => {
              if (cert && cert.id && !allMap.has(cert.id)) {
                allMap.set(cert.id, cert);
              }
            });
          }
        } catch {
          // ignore
        }
      }
    }
  } catch (err) {
    console.warn('Error accessing localStorage for certificates scan:', err);
  }

  return Array.from(allMap.values());
}

/**
 * Evaluates a list of certificates to determine if any document expires in less than 30 days.
 */
export function evaluateCertificatesAlerts(
  certificates: CertificateRecord[],
  thresholdDays: number = 30,
  costCenters?: CostCenter[]
): CertificatesAlertEvaluation {
  const expiringCertificates: EvaluatedCertificateItem[] = [];
  const expiredCertificates: EvaluatedCertificateItem[] = [];
  let validCount = 0;
  let inAuditCount = 0;

  const costCenterMap = new Map<string, string>();
  if (costCenters) {
    costCenters.forEach(cc => costCenterMap.set(cc.id, cc.name));
  }

  certificates.forEach(cert => {
    const validity = getCertificateValidityInfo(cert.validUntil, cert.status, thresholdDays);
    const ccName = cert.costCenterName || (cert.costCenterId ? costCenterMap.get(cert.costCenterId) : '') || 'Sede Operativa Bajío';

    const item: EvaluatedCertificateItem = {
      certificate: cert,
      validity,
      costCenterName: ccName
    };

    if (validity.computedStatus === 'en_auditoria') {
      inAuditCount++;
    } else if (validity.computedStatus === 'vencido') {
      expiredCertificates.push(item);
    } else if (validity.computedStatus === 'proximo_vencer') {
      expiringCertificates.push(item);
    } else {
      validCount++;
    }
  });

  // Sort expiring soonest first (ascending daysRemaining)
  expiringCertificates.sort((a, b) => a.validity.daysRemaining - b.validity.daysRemaining);
  
  // Sort expired by most recently expired first
  expiredCertificates.sort((a, b) => b.validity.daysRemaining - a.validity.daysRemaining);

  const urgentCertificates = [...expiredCertificates, ...expiringCertificates];
  const expiringCount = expiringCertificates.length;
  const expiredCount = expiredCertificates.length;
  const urgentTotalCount = urgentCertificates.length;
  const hasUrgentAlerts = urgentTotalCount > 0;
  const mostUrgentItem = urgentCertificates.length > 0 ? urgentCertificates[0] : null;

  return {
    totalCertificates: certificates.length,
    expiringCount,
    expiredCount,
    urgentTotalCount,
    validCount,
    inAuditCount,
    expiringCertificates,
    expiredCertificates,
    urgentCertificates,
    hasUrgentAlerts,
    mostUrgentItem,
    evaluationTimestamp: new Date().toISOString()
  };
}

/**
 * Notifies the application when certificates are created, edited or deleted so all listeners react.
 */
export function dispatchCertificatesUpdatedEvent(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('snf_certificates_updated'));
    }
  } catch (e) {
    console.warn('Could not dispatch certificates updated event', e);
  }
}
